import { useContext, useMemo } from "react"

import { type BaseCommunityFragment, useCommunityLandingQuery } from "../generated/graphql"
import { graphQLClient } from "../graphql/client"
import { UserContext } from "../providers/UserProvider"
import { useCommunityCustomization } from "./useCommunityCustomization"

export const useCommunityFromCode = () => {
  const { communities } = useContext(UserContext)
  const customizationData = useCommunityCustomization()

  // First try to get from user's memberships
  const memberCommunity = communities.find((c) => c.code === customizationData?.code)

  // Fallback: fetch via CommunityLanding if user is not a member
  const { data: communityLandingData } = useCommunityLandingQuery(
    graphQLClient,
    { communityCode: customizationData?.code ?? "" },
    { enabled: !!customizationData?.code && !memberCommunity },
  )

  // Memoize fallback community to prevent infinite re-renders
  const landingCommunity = communityLandingData?.communityLanding?.community
  const fallbackCommunity = useMemo<BaseCommunityFragment | undefined>(() => {
    if (!landingCommunity) return undefined
    return {
      __typename: "Community",
      id: landingCommunity.id,
      name: landingCommunity.name,
      code: landingCommunity.code ?? null,
      iconUrl: landingCommunity.iconUrl,
      currency: (landingCommunity.currency as BaseCommunityFragment["currency"]) ?? "SEK",
      landingCode: null,
      availablePaymentMethods: landingCommunity.availablePaymentMethods ?? [],
      preselectedPaymentMethod:
        (landingCommunity.preselectedPaymentMethod as BaseCommunityFragment["preselectedPaymentMethod"]) ??
        null,
      configs: landingCommunity.configs ?? null,
      stripeConnectedAccountId: landingCommunity.stripeConnectedAccountId ?? null,
      joinRequirements: landingCommunity.joinRequirements ?? null,
      requiredUserFields: [],
      missingRequiredUserFields: [],
      requestToJoin: landingCommunity.requestToJoin ?? false,
    }
  }, [landingCommunity])

  const community = memberCommunity ?? fallbackCommunity

  return { community }
}
